<template>
  <v-row justify="end" id="ticket-scale-data">
    <v-col cols="auto">
      <span class="subtitle-1">
        {{ title }}
      </span>
    </v-col>

    <v-spacer />

    <v-col cols="auto" v-if="showGroupByToggle">
      <Icon :icon="groupByIcon" :tooltipText="groupByTooltip" @icon-clicked="groupByToggled" :small="false"></Icon>
    </v-col>

    <v-col cols="12"
      v-if="groupByLog">
      <LoadableComponent :loading="loading">
        <TicketScaleDataByLog
        :logs="logs"
        :logHeaders="logHeaders"
        :segmentHeadersGroupedByLog="segmentHeadersGroupedByLog"
        :rateSheets="rateSheets"
        @rate-sheet-detail-clicked="rateSheetDetailClicked"
        />
      </LoadableComponent>
    </v-col>

    <v-col cols="12" v-else>
      <LoadableComponent :loading="loading">
        <TicketScaleDataBySegment
        :segments="segments"
        :segmentHeadersGroupedBySegment="segmentHeadersGroupedBySegment"
        :rateSheets="rateSheets"
        @rate-sheet-detail-clicked="rateSheetDetailClicked"
        />
      </LoadableComponent>
    </v-col>

    <Dialog
    v-if="rateSheetDialog"
    :stateId="dialogId"
    @close="dialogClosed"
    @click:outside="dialogClosed"
    @dialog-closing="dialogClosed">

      <RateSheetViewer
      v-if="rateSheetDialog"
      :rateSheet="rateSheets.get(viewingRateSheetForAmount.rateSheetId)"
      :emphasizedLineId="viewingRateSheetForAmount.rateSheetLineId"
      :activity="{ activityTemplate: { name: viewingRateSheetForAmount.activity } }"
      :accountId="purchasingAccountId"
      :tableHeightCalculationData="tableHeightCalculationData">
        <template #prepend-dialog-actions v-if="$vuetify.breakpoint.mdAndUp">
          <v-col cols="auto">
            <v-row align="center" dense>
              <v-col cols="auto">
                <v-chip color="white" label>
                  <span>{{ $t('log') }}: {{ focusedLogOrSegment?.logNumber }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto" v-if="includeSegmentNumberOnRateSheet">
                <v-chip color="white" label>
                  <span>{{ $t('segment') }}: {{ focusedLogOrSegment?.segmentNumber }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto">
                <v-chip color="white" label>
                  <Icon iconColor="secondary" icon="mdi-ruler" :tooltipText="$t('length')" margin="mr-1" :small="false"/>
                  <span>{{ lengthForRateSheetDisplay }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto">
                <v-chip color="white" label>
                  <Icon iconColor="secondary" icon="mdi-diameter" :tooltipText="$t('diameter')" margin="mr-1" :small="false"/>
                  <span>{{ focusedLogOrSegment?.diameter }}</span>
                </v-chip>
              </v-col>
            </v-row>
          </v-col>
        </template>

        <template #card-body-prepend v-else>
          <v-col cols="12" class="pt-0">
            <v-row align="center" justify="end" dense>
              <v-col cols="auto">
                <v-chip label>
                  <span>{{ $t('log') }}: {{ focusedLogOrSegment?.logNumber }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto" v-if="includeSegmentNumberOnRateSheet">
                <v-chip label>
                  <span>{{ $t('segment') }}: {{ focusedLogOrSegment?.segmentNumber }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto">
                <v-chip label>
                  <Icon iconColor="secondary" icon="mdi-ruler" :tooltipText="$t('length')" margin="mr-1" :small="false"/>
                  <span>{{ lengthForRateSheetDisplay }}</span>
                </v-chip>
              </v-col>

              <v-col cols="auto">
                <v-chip label>
                  <Icon iconColor="secondary" icon="mdi-diameter" :tooltipText="$t('diameter')" margin="mr-1" :small="false"/>
                  <span>{{ focusedLogOrSegment?.diameter }}</span>
                </v-chip>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </RateSheetViewer>
    </Dialog>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatMoney } from '@/utils/NumericMutations'
import { uuidv4 } from '@/utils/NumericMutations.js'
import { AccountingCategory, VolumePricingMode } from '@/utils/Enumerations'
import IconHelpers from '@/utils/IconHelpers'
import { sumBy, sortBy, sortedUniqBy, cloneDeep } from 'lodash'
import { uniqueDialogId } from '@/utils/componentHelpers'

const GroupBy = {
  Log: 0,
  Segment: 1
}

export default {
  name: 'TicketScaleData',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    RateSheetViewer: () => import('@/components/activity/rate-sheet/RateSheetViewer.vue'),
    TicketScaleDataByLog: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataByLog.vue'),
    TicketScaleDataBySegment: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataBySegment.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    ticket: {
      type: Object,
      required: true
    },
    financials: {
      type: Object,
      required: false,
      default: undefined
    },
    scaleDataSource: {
      type: Number,
      required: false,
      default: null
    },
    scaleData: {
      type: Object,
      required: true
    },
    rateSheets: {
      type: Map,
      required: true
    }
  },

  data: () => ({
    VolumePricingMode,
    IconHelpers,
    AccountingCategory,
    GroupBy,
    rateSheetDialog: false,
    dialogId: uniqueDialogId('ticket-scale-data'),
    loading: true,
    logs: null,
    segments: null,
    groupBy: GroupBy.Segment,
    expandedSegments: [],
    openLogPanel: null,
    viewingRateSheetForAmount: null,
    accountScaleData: new Map(),
    tableHeight: 'auto',
    tractGroup: undefined
  }),

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('user', ['businessEntities']),
    title () {
      return this.$t('scaleDataBy', { by: this.$t(this.groupByLog ? 'log' : 'segment') })
    },

    groupByLog () {
      return this.groupBy === GroupBy.Log
    },

    showGroupByToggle () {
      return this.scaleData && this.scaleData.volumePricingMode === VolumePricingMode.WholeLengthTopDiameter.value
    },

    groupByTooltip () {
      return this.$t('groupByCategory', { category: this.$t(this.groupByLog ? 'segment' : 'log') })
    },

    groupByIcon () {
      return !this.groupByLog
        ? 'by-log'
        : 'by-segment'
    },

    logHeaders () {
      return [
        { text: '', value: 'attributes', width: 48, ordinal: 0 },
        { text: this.$t('log'), value: 'logNumber', ordinal: 1, align: 'right', width: 64 },
        { text: this.$t('species'), value: 'species', ordinal: 3 },
        { text: this.$t('grade'), value: 'grade', ordinal: 4 },
        { text: this.$t('length'), value: 'length', align: 'right', ordinal: 5 },
        { text: this.$t('diameter'), value: 'diameter', align: 'right', ordinal: 6 },
        { text: this.$t('defect'), value: 'defectDefinition', ordinal: 7 },
        { text: this.$t('cut'), value: 'cut', align: 'center', sortable: false, ordinal: 8 },
        { text: this.$t('grossVolume'), value: 'grossVolume', align: 'right', ordinal: 9 },
        { text: this.$t('netVolume'), value: 'netVolume', align: 'right', ordinal: 10 }
      ]
    },

    segmentHeadersGroupedByLog () {
      return [
        { text: this.$t('segment'), value: 'segmentNumber', ordinal: 2, align: 'right', width: 64 },
        { text: this.$t('species'), value: 'species', ordinal: 3 },
        { text: this.$t('grade'), value: 'grade', ordinal: 4 },
        { text: this.$t('length'), value: 'length', align: 'right', ordinal: 5 },
        { text: this.$t('diameter'), value: 'diameter', align: 'right', ordinal: 6 },
        { text: this.$t('grossVolume'), value: 'grossVolume', align: 'right', ordinal: 9 },
        { text: this.$t('netVolume'), value: 'netVolume', align: 'right', ordinal: 10 }
      ]
    },

    segmentHeadersGroupedBySegment () {
      const sorted = sortBy([
        ...this.segmentHeadersGroupedByLog,
        ...this.logHeaders,
        {
          text: this.$t('amounts'),
          value: 'amounts',
          align: 'right',
          ordinal: 11
        }], 'ordinal')
      return sortedUniqBy(sorted, 'value')
    },

    focusedLogOrSegment () {
      if (this.groupByLog) {
        return this.focusedLog
      } else {
        return this.focusedSegment
      }
    },

    focusedLog () {
      const log = this.logs[this.openLogPanel]
      const segment = log.segments.find(s => s.amounts.length > 0)
      return {
        logNumber: log.logNumber,
        segmentNumber: segment.segmentNumber,
        species: segment.species,
        segmentLength: segment.length,
        length: sumBy(log.segments, s => parseInt(s.length)).toString().concat('\''),
        diameter: segment.diameter
      }
    },

    focusedSegment () {
      return this.segments.find(segment => segment.uuid === this.expandedSegments[0]?.uuid)
    },

    includeSegmentNumberOnRateSheet () {
      return [VolumePricingMode.SegmentLengthSegmentTop.value,
        VolumePricingMode.WholeLengthSegmentDiameter.value].includes(this.scaleData?.volumePricingMode)
    },

    tableHeightCalculationData () {
      return {
        auto: this.$vuetify.breakpoint.mdAndDown,
        offset: this.$vuetify.breakpoint.lgAndUp ? 180 : undefined
      }
    },

    lengthForRateSheetDisplay () {
      const showSegmentLength = this.includeSegmentNumberOnRateSheet
      if (showSegmentLength) return this.focusedLogOrSegment?.segmentLength

      const groupedByLog = this.groupByLog
      if (groupedByLog) return this.focusedLogOrSegment?.length

      const segmentsForLog = this.segments.filter(s => s.logNumber === this.focusedLogOrSegment?.logNumber)
      const length = sumBy(segmentsForLog, s => parseInt(s.length))
      return `${length}'`
    },

    purchasingAccountId () {
      if (this.ticket.tractGroupId == null) return this.ticket.destinationAccountId
      return this.businessEntities.find(be => be.businessEntityId === this.tractGroup?.businessEntityId)?.accountId
    }
  },

  watch: {
    openLogPanel () {
      this.expandedSegments.length = 0
    }
  },

  async mounted () {
    await this.init()
  },

  methods: {
    formatMoney,
    sumBy,

    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract', ['fetchTractGroup']),
    ...mapActions('user', ['fetchAllBusinessEntities']),

    async init () {
      this.loading = true
      this.initSegments()
      this.initLogs()
      this.groupBy = VolumePricingMode.WholeLengthTopDiameter.value === this.scaleData.volumePricingMode
        ? GroupBy.Log
        : GroupBy.Segment
      this.loading = false
    },

    initSegments () {
      let segments = cloneDeep(this.scaleData.segmentVolumes)
      segments = segments.map(this.segmentMapper)
      segments.sort((s1, s2) => {
        let result = s1.logNumber - s2.logNumber
        if (result === 0) result = s1.segmentNumber - s2.segmentNumber
        return result
      })
      this.segments = segments
    },

    segmentMapper (segment) {
      return {
        ...segment,
        diameter: `${segment.diameter}"`,
        length: `${segment.length}'`,
        amounts: segment.amounts.map(this.segmentAmountMapper),
        uuid: uuidv4().substring(0, 8),
        type: 'segment'
      }
    },

    segmentAmountMapper (amount) {
      const rs = (amount.rateSheetId > 0) ? this.rateSheets.get(amount.rateSheetId) : null
      return {
        ...amount,
        financial: this.getFinancialObject(amount),
        rateSheetName: rs ? rs.name : null,
        computedAtRate: (amount.rateSheetId > 0)
          ? rs.rateSheetLines
            .find(rsl => rsl.rateSheetLineId === amount.rateSheetLineId)?.rate
          : null
      }
    },

    getFinancialObject (amount) {
      const { payables, receivables, journalEntries } = this.financials || {}
      let financialsOfCategory
      switch (amount.type) {
        case AccountingCategory.Payable.value: financialsOfCategory = payables; break
        case AccountingCategory.Receivable.value: financialsOfCategory = receivables; break
        case AccountingCategory.Accrual.value: financialsOfCategory = journalEntries; break
        default: return undefined
      }
      return financialsOfCategory.find(f => f.activityId === amount.activityId)
    },

    initLogs () {
      const segmentsGroupedByLog = Object.groupBy(this.segments, (s) => s.logNumber)
      const logs = Object.entries(segmentsGroupedByLog).map(([logNumber, segments]) => {
        return {
          logNumber,
          species: segments[0].species,
          grade: segments[0].grade,
          defectDefinition: segments[0].defectDefinition,
          defectDefinitionId: segments[0].defectDefinitionId,
          buttLog: segments.some(s => s.buttLog),
          lengthCut: sumBy(segments, (s) => s.lengthCut),
          diameterCut: sumBy(segments, (s) => s.diameterCut),
          volumeCut: sumBy(segments, (s) => s.volumeCut),
          grossVolume: sumBy(segments, (s) => s.grossVolume),
          netVolume: sumBy(segments, (s) => s.netVolume),
          length: `${sumBy(segments, (s) => Math.max(0, parseInt(s.length)))}'`,
          diameter: (segments.find(s => s.segmentNumber === 1) ?? segments[0]).diameter,
          segments: cloneDeep(segments),
          type: 'log'
        }
      })
      logs.sort((l1, l2) => parseInt(l1.logNumber) - parseInt(l2.logNumber))
      this.logs = logs
    },

    groupByToggled () {
      this.groupBy = this.groupBy === GroupBy.Log ? GroupBy.Segment : GroupBy.Log
      this.expandedSegments = []
      this.openLogPanel = null
    },

    dialogClosed () {
      this.rateSheetDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async rateSheetDetailClicked (info) {
      if (!info) return
      if (this.ticket.tractGroupId != null) {
        this.tractGroup = await this.fetchTractGroup(this.ticket.tractGroupId)
      }
      if (this.businessEntities.length === 0) {
        await this.fetchAllBusinessEntities()
      }
      this.viewingRateSheetForAmount = info.tableRow
      this.rateSheetDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '90vw' })
      if (info.panel != null) this.openLogPanel = info.panel
      this.expandedSegments = info.segments || []
    }
  }
}
</script>

<style>
.v-application #ticket-scale-data .small-table-header {
  font-size: .925rem !important;
  font-weight: 500;
  font-family: Roboto, sans-serif;
}

.v-application #ticket-scale-data .v-expansion-panels {
  overflow-x: clip !important;
  overflow-clip-margin: 16px !important;
  overflow-y: auto;
}

#ticket-scale-data .v-tabs-bar {
  background-color: transparent;
}

#ticket-scale-data .v-tab--active {
  background-color: white;
}
</style>
